import GridLayout, { WidthProvider, type Layout } from "react-grid-layout";
import { GripVertical, MousePointerClick, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

import { type ReportPanel } from "../api";
import { type ColumnFilter } from "../viz";
import { PanelView } from "./PanelView";

import "react-grid-layout/css/styles.css";

const ResponsiveGrid = WidthProvider(GridLayout);

/**
 * The dashboard canvas: lays out saved panels on a 12-col grid. When `editable`,
 * panels drag (by their header handle only, so chart clicks still drill) and
 * resize, and layout changes persist via `onLayoutPersist` on drop/resize-end.
 * Read-only otherwise (the viewer for non-editors). Renders each panel through
 * the shared {@link PanelView}.
 */
export function DashboardGrid({
  panels,
  editable = false,
  onLayoutPersist,
  onRemove,
  params,
  columnFilters,
  reportId,
  cached = false,
}: {
  panels: ReportPanel[];
  editable?: boolean;
  onLayoutPersist?: (panels: ReportPanel[]) => void;
  onRemove?: (panelId: string) => void;
  /** Dashboard filter values, forwarded to each panel's query. */
  params?: Record<string, string>;
  /** Active column auto-filters, applied to each panel's result rows. */
  columnFilters?: ColumnFilter[];
  /** The report id — enables server-cached panel reads in the viewer. */
  reportId?: string;
  /** Read cached panel data (viewer) rather than running live (see PanelView). */
  cached?: boolean;
}) {
  const layout: Layout[] = panels.map((p, i) => ({
    i: p.id,
    x: p.layout?.x ?? (i % 2) * 6,
    y: p.layout?.y ?? Math.floor(i / 2) * 8,
    w: p.layout?.w ?? 6,
    h: p.layout?.h ?? 8,
    minW: 2,
    minH: 3,
  }));

  const persist = (next: Layout[]) => {
    if (!onLayoutPersist) return;
    const updated = panels.map((p) => {
      const gi = next.find((l) => l.i === p.id);
      return gi ? { ...p, layout: { x: gi.x, y: gi.y, w: gi.w, h: gi.h } } : p;
    });
    onLayoutPersist(updated);
  };

  return (
    <ResponsiveGrid
      className="w-full"
      layout={layout}
      cols={12}
      rowHeight={40}
      margin={[12, 12]}
      isDraggable={editable}
      isResizable={editable}
      // 's' = height-only (rows), 'e' = width-only (cols), 'se' = both.
      resizeHandles={["s", "e", "se"]}
      draggableHandle=".panel-drag-handle"
      draggableCancel=".no-drag"
      compactType="vertical"
      onDragStop={persist}
      onResizeStop={persist}
    >
      {panels.map((p) => (
        <div
          key={p.id}
          className="flex flex-col overflow-hidden rounded-lg border border-border bg-card"
        >
          <div
            className={cn(
              "panel-drag-handle flex items-center gap-2 border-b border-border px-3 py-1.5",
              editable && "cursor-move",
            )}
          >
            {editable && <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            <span className="min-w-0 flex-1 truncate text-xs font-semibold">
              {p.title || "Untitled panel"}
            </span>
            {p.viz.drilldown && (
              <Badge variant="info" title={`Clicks open ${p.viz.drilldown.route}`}>
                <MousePointerClick className="h-3 w-3" />
              </Badge>
            )}
            {editable && onRemove && (
              <button
                type="button"
                onClick={() => onRemove(p.id)}
                className="no-drag text-muted-foreground hover:text-danger"
                title="Remove panel"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="min-h-0 flex-1 p-2">
            <PanelView
              panel={p}
              className="h-full"
              params={params}
              columnFilters={columnFilters}
              reportId={reportId}
              cached={cached}
            />
          </div>
        </div>
      ))}
    </ResponsiveGrid>
  );
}
